import { compose } from 'recompose'
import _ from 'lodash'
import { Helmet } from 'react-helmet'
import { Toast, Loader, Tip } from '@vzmi/shp_shared_compo'
import { FetchDataUtil, ExcelExport, ExcelExportHelper } from '@vzmi/component_ec_nodejs_util'
import styles from './SalesPerformance.css'
import { sendI13nClickEvent } from '../../../lib/i13nUtil'
import safeAwait from '../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../lib/errorHelper'
import withConnectedExport from '../../containers/Common/WithConnectedExport'
import SPBaseModule from '../../lib/SPBaseModule'
import {
  getDefaultTimezoneMoment,
  numeralFormat,
  dollarFormat
} from '../../../lib/renderUtil'
import {
  DEFAULT_NO_FOUND_DESCRIPTION
} from '../../constants/globalTypes'
import { withI13nWrapper } from '../Common/I13nWrapper'
import { IconExportButton } from '../Common/ExportButton'
import Section from '../Common/ItemPage/Section/Section'
import TableAction from '../Common/NewDashboard/Table/TableAction/TableAction'
import Sort from '../Common/NewDashboard/Sort/Sort'
import RangeSummary from '../Common/NewDashboard/RangeSummary/RangeSummary'
import {
  RANKING_SIZE,
  SORT_OPTIONS,
  transformValuesToBestSellersParams,
  transformSellersRankings
} from '../Dashboard/OperatingDashboard/SalesPerformance/SalesPerformance'
import { FIELD as SEARCH_FIELD } from '../Dashboard/OperatingDashboard/SalesPerformance/SearchForm/SearchForm'
import RankingTable, {
  FIELD as TABLE_FIELD,
  FIELD_MAP as TABLE_FIELD_MAP
} from '../Dashboard/OperatingDashboard/SalesPerformance/RankingTable/RankingTable'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from './SalesPerformance.ga'
import { HistoryUtil } from '@vzmi/component_ec_nodejs_util/dist/lib'

const EXCEL_SORT_COLUMNS = [
  TABLE_FIELD.LISTING_ID,
  TABLE_FIELD.NAME,
  TABLE_FIELD.SOLD_AMOUNT,
  TABLE_FIELD.SOLD_QUANTITY,
  TABLE_FIELD.AVAILABLE_QUANTITY
]

const ExportHelper = new ExcelExportHelper({
  headers: {
    ranking: '排行',
    ...(EXCEL_SORT_COLUMNS.reduce((map, field) => {
      map[field] = TABLE_FIELD_MAP[field]
      return map
    }, {}))
  },
  fileName: 'TopSellingItems_{{ nowDate.format("YYYYMMDDhhmmss") }}',
  bookType: 'xlsx'
})

export const PER_PAGE_SIZE = 20

export const EXPORT_MAX_SIZE = 1000

export const getCurrentOffset = page => PER_PAGE_SIZE * (page - 1)

export const getParams = state => {
  const { currentPage, gdids } = state
  const offset = getCurrentOffset(currentPage)
  let params = transformValuesToBestSellersParams({}, state)
  params = {
    ...params,
    offset,
    limit: PER_PAGE_SIZE,
    ...(gdids ? { [SEARCH_FIELD.LISTING_IDS]: gdids } : {})
  }
  return params
}

export const transformToSheetData = (bestsellers) => {
  const sheetData = _.map(bestsellers, item => {
    return {
      ...item,
      soldAmount: dollarFormat(item.soldAmount),
      soldQuantity: numeralFormat(item.soldQuantity, { allowZero: true }),
      availableQuantity: numeralFormat(item.availableQuantity, { allowZero: true })
    }
  })
  return sheetData
}

class SalesPerformance extends SPBaseModule {
  constructor (props) {
    super(props)
    const { gdids = '' } = HistoryUtil.retrieveQueryParams(this.props)
    this.state = {
      currentPage: 1,
      rankingSortType: SORT_OPTIONS[0].value,
      resultsTotal: 0,
      range: [],
      sellersRankings: [],
      rankingColumnProps: {
        rankingSize: RANKING_SIZE,
        width: 35,
        startIndex: 0
      },
      paginationProps: {},
      gdids
    }
  }

  componentDidMount () {
    const { i13nMethods: { sendI13nPageViewByPageKey } } = this.props
    this.updateRankingTable(this.state)
    sendI13nPageViewByPageKey('salesPerformance')
  }

  updateRankingTable = state => this.loading(async () => {
    const [errorMessage, payload] = await this.getBestsellers(state)
    if (errorMessage) {
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }

    const {
      bestsellers: sellersRankings = [],
      pagination,
      reportDateStart,
      reportDateEnd
    } = payload
    const range = [reportDateStart, reportDateEnd]
    const resultsTotal = _.get(pagination, 'resultsTotal')
    const paginationProps = {
      total: resultsTotal,
      onChange: this.onPageChange
    }
    const convertedSellersRankings = transformSellersRankings(sellersRankings, PER_PAGE_SIZE)
    this.setState({
      sellersRankings: convertedSellersRankings,
      paginationProps,
      range,
      resultsTotal
    })
    this.setIsSuccess(true)
  })

  getBestsellers = async (state) => {
    const { fetchPaginationBestsellers } = this.props.actions
    const params = getParams(state)
    const [error, payload] = await safeAwait(
      fetchPaginationBestsellers(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      return [errorMessage]
    }

    return [undefined, payload]
  }

  onRankingTableSortChange = event => {
    const previousValue = this.state.rankingSortType
    const newValue = _.get(event, 'target.value', '')
    if (newValue === previousValue) {
      return
    }
    this.sendPageview({
      type: GA_CLICK_ACTION.TABLE_SORT,
      payload: newValue
    })

    const newState = {
      ...this.state,
      rankingSortType: newValue
    }
    this.setState(newState)
    this.updateRankingTable(newState)
  }

  onPageChange = page => {
    const { rankingSortType, rankingColumnProps } = this.state
    const newState = {
      ...this.state,
      currentPage: page,
      rankingSortType,
      rankingColumnProps: {
        ...rankingColumnProps,
        startIndex: getCurrentOffset(page)
      }
    }
    this.sendPageview({
      type: GA_CLICK_ACTION.TABLE_PAGINATION
    })
    this.setState(newState)
    this.updateRankingTable(newState)
  }

  exportExcel = async () => {
    const {
      openExportModal,
      closeExportModal
    } = this.props

    this.sendPageview({
      type: GA_CLICK_ACTION.EXPORT_EXCEL
    })
    openExportModal()

    const [error, payload] = await this.getTotalPaginationBestsellers()
    if (error) {
      closeExportModal({ isError: true })
      return
    }

    const bestsellers = _.get(payload, 'data', [])
    const sheetData = transformToSheetData(
      transformSellersRankings(bestsellers, PER_PAGE_SIZE)
    )
    const option = {
      fileName: ExportHelper.getFileName({
        nowDate: getDefaultTimezoneMoment()
      }),
      sheets: [
        {
          name: ExportHelper.getSheetName(),
          headers: ExportHelper.getSheetHeaders(),
          data: ExportHelper.convertDataByHeaders(sheetData)
        }
      ]
    }
    const ExcelExporter = new ExcelExport(option)
    ExcelExporter.download({ bookType: ExportHelper.getBookType() })
    closeExportModal()
  }

  getTotalPaginationBestsellers = async () => {
    const { fetchPaginationBestsellers } = this.props.actions
    const params = {
      ...getParams(this.state),
      maxResults: EXPORT_MAX_SIZE,
      currentPage: 1
    }
    const [error, payload] = await safeAwait(
      FetchDataUtil.fetchAllPages(params => {
        return fetchPaginationBestsellers(params)
          .then(res => {
            if (res.error) {
              throw res
            }
            return res
          })
      }, {
        payload: params,
        selector: res => _.get(res, 'bestsellers', [])
      })
    )
    if (error) {
      return [error]
    }

    return [undefined, payload]
  }

  sendPageview = (action) => {
    const { i13n } = this.props
    const i13nModel = getI13nModel(action)
    sendI13nClickEvent({ i13n, i13nModel })
  }

  sendTableLinkPageview = payload => {
    this.sendPageview({
      type: GA_CLICK_ACTION.TABLE_LINK,
      payload
    })
  }

  render () {
    const {
      text: title,
      supplier
    } = this.props
    const {
      isLoading,
      isError,
      rankingSortType,
      sellersRankings,
      resultsTotal,
      range,
      rankingColumnProps,
      paginationProps
    } = this.state
    const disableExportBtn = (
      isLoading ||
      (resultsTotal === 0)
    )

    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <Section
          title={(
            <div>
              即時業績-賣場排行
              <IconExportButton
                disabled={disableExportBtn}
                data-role='export-button'
                className={styles.exportBtn}
                onClick={this.exportExcel}
                label='下載整筆檔案'
              />
              <Tip
                type='info'
                className={styles.tips}
              >
                最多下載前 1000 筆
              </Tip>
            </div>
          )}
        >
          <Loader loaded={!isLoading}>
            {
              isError
                ? this.renderIOError()
                : (
                  <>
                    <RangeSummary range={range} />
                    <TableAction
                      className={styles.tableAction}
                      leftContent={(
                        <Tip
                          type='info'
                          className={styles.tips}
                        >
                          {`共 ${resultsTotal} 筆`}
                        </Tip>
                      )}
                    >
                      <Sort
                        id='sales-performance'
                        options={SORT_OPTIONS}
                        onChange={this.onRankingTableSortChange}
                        sortType={rankingSortType}
                      />
                    </TableAction>
                    <RankingTable
                      sharedLink={supplier.sharedLink}
                      data={sellersRankings}
                      rankingColumnProps={rankingColumnProps}
                      noFoundDescription={DEFAULT_NO_FOUND_DESCRIPTION}
                      paginationProps={paginationProps}
                      sendTableLinkPageview={this.sendTableLinkPageview}
                      showPagination
                    />
                  </>
                )
            }
          </Loader>
        </Section>
      </>
    )
  }
}

const hoc = compose(
  withI13nWrapper,
  withConnectedExport
)

export default hoc(SalesPerformance)
